import { SearchNormal1 } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { EmptyState, Text } from '../system';
import type { InstrumentFallbackRoute } from './TestSelector';

interface TestSelectorEmptyStateProps {
    /**
     * The catalog *did* return instruments and the "desfavoráveis" checkbox is
     * hiding them. A different situation with a different answer, so it gets a
     * different message: the fix is the filter, not a new instrument.
     */
    hiddenByUnfavorableFilter: boolean;
    /** Whether the two non-catalog routes are reachable from here at all. */
    canLeaveCatalog: boolean;
    /** Checked instruments the user cannot see, because the search moved on. */
    hasPendingSelection: boolean;
    onIncludeUnfavorable: () => void;
    onFallbackRoute: (route: InstrumentFallbackRoute) => void;
}

/**
 * What the catalog says when it has nothing to show.
 *
 * The whole point is the last line: a search that finds nothing used to be a
 * dead end, and the instrument the psychologist is looking for may simply not
 * be in the SATEPSI — in which case describing it, or pasting the table they
 * already corrected, is the right answer rather than a consolation prize.
 */
export default function TestSelectorEmptyState({
    hiddenByUnfavorableFilter,
    canLeaveCatalog,
    hasPendingSelection,
    onIncludeUnfavorable,
    onFallbackRoute,
}: TestSelectorEmptyStateProps) {
    // Inside a dialog the default dashed card and Lottie read as a second
    // surface stacked on the first; same treatment the patient picker uses.
    const chrome = {
        visual: 'icon',
        icon: SearchNormal1,
        className: 'border-0 bg-transparent px-0 py-8 shadow-none',
    } as const;

    if (hiddenByUnfavorableFilter) {
        return (
            <EmptyState
                {...chrome}
                title="Só há instrumentos com parecer desfavorável"
                description="Essa busca só retornou instrumentos que o SATEPSI avaliou como desfavoráveis."
                action={
                    <Button variant="outline" onClick={onIncludeUnfavorable}>
                        Incluir desfavoráveis
                    </Button>
                }
            />
        );
    }

    return (
        <EmptyState
            {...chrome}
            title="Nenhum instrumento encontrado no SATEPSI"
            description={
                canLeaveCatalog
                    ? 'Reveja o nome buscado e o filtro de domínio. Se o instrumento não estiver no catálogo, monte a tabela dele aqui mesmo.'
                    : 'Reveja o nome buscado e o filtro de domínio.'
            }
            action={
                canLeaveCatalog ? (
                    <div className="flex flex-col gap-2 sm:flex-row">
                        <Button onClick={() => onFallbackRoute('tool_test_free')}>
                            Criar instrumento livre
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => onFallbackRoute('tool_test_paste')}
                        >
                            Colar uma tabela pronta
                        </Button>
                    </div>
                ) : hasPendingSelection ? (
                    // Leaving would unmount the list and drop what is already
                    // checked without a word — a selection survives a search,
                    // so it can be sitting here unseen.
                    <Text variant="description">
                        Termine de adicionar o que já está selecionado antes de criar um instrumento
                        fora do catálogo.
                    </Text>
                ) : undefined
            }
        />
    );
}
